import { updateAttributes } from '../shared/api/shared-app-requests';
import { TrackingData, TrackingEventType } from '../shared/types/Tracking';
import { Offer, Product } from '../shared/types';
import { parseJsonDeprecated } from '../shared/api/utils';
import TrackingManager from '../shared/product-page-placement/tracking-manager';
import { getHandleForProductOrItemIdForService } from './offer-utils';
import timeLimit from 'time-limit-promise';
import { captureException } from '@sentry/browser';

const TRACK_CHECKOUT_VIEW = '/checkout/track/view';
const TRACK_THANK_YOU_PAGE_VIEW = '/thank-you-page/track/view';

export async function trackAddToCart(
  offer: Offer,
  parentProduct: Product,
  productId?: number,
) {
  const token = await TrackingManager.getInstance()._getCartToken();
  await updateAttributes({
    candyrack_token: token,
  });

  let addToCartHandleData = getHandleForProductOrItemIdForService(offer);
  return track({
    event_type: TrackingEventType.ADD_TO_CART,
    cart_token: token,
    line_items: [
      {
        offer_id: offer.id,
        variant_id: offer.shopifyProductVariantIdComputedInFE,
        product_id: productId || offer.shopifyProductIdComputedInFE,
        handle: addToCartHandleData,
        parent_product: {
          id: parentProduct.id,
          handle: parentProduct.handle,
        },
        quantity: offer.quantityComputedInFE || 1,
      },
    ],
  });
}

export async function trackViewForCandyRackExternalAddToCart(
  offers: { id: number; product_ids: number[] }[],
) {
  const token = await TrackingManager.getInstance()._getCartToken();
  return track({
    event_type: TrackingEventType.VIEW,
    cart_token: token,
    offers,
  });
}

/*
 ** TODO: According to APPS-5551 Jira discussions we can remove this.
 ** We can do so in a separate, dedicated task.
 */
export async function trackRemoveFromCart(offer: Offer) {
  const token = await TrackingManager.getInstance()._getCartToken();

  return track({
    event_type: TrackingEventType.REMOVE_FROM_CART,
    cart_token: token,
    offer: offer.id,
  });
}

function track(data: TrackingData) {
  // There are cases with lots of products, so this takes up to 2 seconds.
  // As we are however awaiting for the response and the popup needs to stay responsive,
  // we need to abort this in such cases in spite of loosing some analytics data.
  const hostname = process.env.REACT_APP_CUSTOMER_API_HOSTNAME;
  const fetchResult = parseJsonDeprecated(
    fetch(`${hostname}api/candyrack/popup/track-offers`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-shop': window.Shopify.shop,
      },
      body: JSON.stringify(data),
    }),
  ).catch((error) => captureException(error));

  return timeLimit(fetchResult, 500);
}

export async function trackCheckout(
  offers: { id: number; product_ids: number[] }[],
) {
  const cartToken = await TrackingManager.getInstance()._getCartToken();

  const payload = {
    preview: false,
    cart_token: cartToken,
    offers,
  };

  const hostname = process.env.REACT_APP_CUSTOMER_API_HOSTNAME;
  const fetchResult = parseJsonDeprecated(
    fetch(`${hostname}api/candyrack${TRACK_CHECKOUT_VIEW}`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-shop': window.Shopify.shop,
      },
      body: JSON.stringify(payload),
    }),
  ).catch((error) => captureException(error));

  return timeLimit(fetchResult, 500);
}

type ThankYouPageTrackingPayload = {
  order_id: number;
  offers: { id: number }[];
};

export async function trackTyPView(payload: ThankYouPageTrackingPayload) {
  const hostname = process.env.REACT_APP_CUSTOMER_API_HOSTNAME;
  const fetchResult = parseJsonDeprecated(
    fetch(`${hostname}api/candyrack${TRACK_THANK_YOU_PAGE_VIEW}`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'x-shop': window.Shopify.shop,
      },
      body: JSON.stringify({ ...payload, preview: false }),
    }),
  ).catch((error) => captureException(error));

  return timeLimit(fetchResult, 500);
}

export async function trackSmartOffersView(
  offers: { id: number; product_ids: number[] }[],
) {
  const token = await TrackingManager.getInstance()._getCartToken();
  await updateAttributes({
    candyrack_token: token,
  });

  return track({
    event_type: TrackingEventType.VIEW,
    cart_token: token,
    offers: offers,
  });
}
